import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgForOf} from "@angular/common";
import {initFlowbite} from "flowbite";
import {Prestazione} from "../../models/prestazione.model";
import {PrenotazioneService} from "../../services/prenotazione.service";
import {Prenotazione} from "../../models/prenotazione.model";
import moment from "moment";
import {catchError, map, of, switchMap, tap, throwError} from "rxjs";
import {PrestazioneService} from "../../services/prestazione.service";
import {ActivatedRoute} from "@angular/router";
import {DayService} from "../../services/day.service";
import {SpinnerComponent} from "../../utils/spinner/spinner.component";
import {SocketService} from "../../services/socket.service";
import {SlotService} from "../../services/slot.service";
import {environment} from "../../../environments/environment";

// CalendarComponent è un componente Angular che gestisce la visualizzazione e l'interazione con un calendario
@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    NgForOf,
    CommonModule,
    SpinnerComponent
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css'
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {

  // Variabili d'ambiente
  date = moment(); // Data corrente
  oraApertua: number = environment.oraApertua;
  oraChiusura: number = environment.oraChiusura;

  // Dati passati dal componente padre
  @Input() prestazione!: Prestazione; // Prestazione selezionata

  // Variabili per i dati del frontend
  isLoading = false; // Flag per il caricamento - TODO da implementare
  titlePrestazione = 'Nome'; // Titolo della prestazione
  stanza = '(descrizione)'; // Descrizione della stanza
  timePosition: number = 0; // Posizione del tempo
  slotDurationMinute: number = 20; // Durata dello slot in minuti (default 20)
  isHighlightRed: boolean = false; // Flag per evidenziare in rosso
  timerInterval: any; // Intervallo del timer

  // Campi calcolati
  slots: any[] = []; // Slot del giorno
  allSlots: any[] = []; // Slot di tutta la settimana
  events: any[] = []; // Eventi del calendario
  allPrenotazioni: any[] = []; // Elenco di tutte le prenotazioni
  endCache: any = null; // Data entro quale la cache a BE finisce

  // Modale per la creaizone di una prenotazione
  @Output() slotSelected = new EventEmitter<{
    prestazione: any,
    date: any,
    hour: number,
    minutes: number,
    isVisible: boolean
  }>();

  // Modale per la modifica di una prenotazione
  @Output() eventSelected = new EventEmitter<{
    prestazione: any,
    prenotazione: any,
    hour: number,
    minutes: number,
    isVisible: boolean
  }>();

  public get conteggioPrenotazioni() {
    const prezzi = this.events.reduce((acc, event) => {
      const prezzo = event.prenotazione?.prezzo;
      return acc + (typeof prezzo === 'number' ? prezzo : 0);
    }, 0);

    const prenotazioni = this.events.length;

    return { prezzi, prenotazioni };
  }


  // Costruttore del componente
  constructor(
    private dayService: DayService, // Servizio per la gestione dei giorni
    public prenotazioneService: PrenotazioneService, // Servizio per le prenotazioni
    public prestazioneService: PrestazioneService, // Servizio per le prestazioni
    private socketService: SocketService, // Servizio per il collegamento a be tramite socket
    private slotService: SlotService,
    private cd: ChangeDetectorRef, // Riferimento al ChangeDetector
    private route: ActivatedRoute, // Rotta attiva
  ) {
  }

  // Metodo chiamato all'inizializzazione del componente
  ngOnInit() {
    // Sottoscrizione ai parametri della rotta
    this.route.paramMap.subscribe(params => {
      if (params.get('day')) {
        this.dayService.changeDay(params.get('day'));
        this.date = moment(params.get('day'));
        this.getSlots();
        this.getPrenotazioni()
      }
    });

    // Inizializzazione di Flowbite
    initFlowbite();
    // Impostazione delle variabili del calendario in base alla prestazione
    this.setCalendarVariablesBasedOnPerformance();
    // Aggiornamento della posizione del tempo ogni minuto
    this.updateTimePositionEveryMinute();

    // Websocket in ascolto per l'inserimento di nuoce prenotazioni
    this.socketService.onCreateMessage(this.prestazione._id)
      .subscribe((response) => {
        //Aggiungo la prenotazione alla lista globale di prenotazioni
        const dayKey = moment(response.prenotazione.inizio).format('YYYY-MM-DD');
        const allPrenotazioniFind = this.allPrenotazioni.find(item => item.day === dayKey);
        if (allPrenotazioniFind) {
          allPrenotazioniFind.prenotazioni.push(response.prenotazione);
        } else {
          this.allPrenotazioni.push({
            day: dayKey,
            prenotazioni: [response.prenotazione]
          });
        }
        // Lo aggiungo alle prenotazioni visibili se è il giorno odierno
        let prenotazioneTemp = this.mapSinglePrenotazione(response.prenotazione);
        if (dayKey === this.date.format('YYYY-MM-DD')) {
          console.log(prenotazioneTemp);
          this.events.push(prenotazioneTemp);
        }
      });

    // Websocket in ascolto per l'inserimento di nuoce prenotazioni
    this.socketService.onUpdateMessage(this.prestazione._id)
      .subscribe((response) => {
        //Aggiungo la prenotazione alla lista globale di prenotazioni
        const dayKey = moment(response.prenotazione.inizio).format('YYYY-MM-DD');
        const allPrenotazioniFind = this.allPrenotazioni.find(item => item.day === dayKey);
        if (allPrenotazioniFind) {
          const index = allPrenotazioniFind.prenotazioni.findIndex((prenotazione: {
            _id: string;
          }) => prenotazione._id === response.prenotazione._id);
          if (index > -1) {
            allPrenotazioniFind.prenotazioni[index] = response.prenotazione;
          }
        }

        // Se presente aggiorno la prenotazione che si trova nella pagina attuale
        const index = this.events.findIndex(e => e.prenotazione._id === response.prenotazione._id);
        if (index > -1) {
          // Sostituisco l’evento esistente con quello aggiornato
          this.events[index] = this.mapSinglePrenotazione(response.prenotazione);
        }
      });

    // Websocket in ascolto per l'eliminazione delle prenotazioni
    this.socketService.onDeleteMessage(this.prestazione._id)
      .subscribe((response) => {
        // Controllo se la prenotazione esiste in quelle recuperate totali, se esiste la elimino
        const dayKey = moment(response.prenotazione.inizio).format('YYYY-MM-DD');
        const allPrenotazioniFind = this.allPrenotazioni.find(item => item.day === dayKey);

        if (allPrenotazioniFind) {
          allPrenotazioniFind.prenotazioni = allPrenotazioniFind.prenotazioni.filter(
            (p: { _id: any; }) => p._id !== response.prenotazione._id
          );
        }

        // Elimino dalle prenotazioni odierne se esiste
        this.events = this.events.filter(event => event.prenotazione._id !== response.prenotazione._id);
      });
  }

  // Metodo chiamato dopo l'inizializzazione del componente
  ngAfterViewInit(): void {
    // Aggiornamento della posizione del tempo (linea rossa)
    this.updateTimePosition();
  }

  // Metodo chiamato alla distruzione del componente
  ngOnDestroy() {
    // Pulizia dell'intervallo del timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  /*
  Chiamate a BE tramite service
  */

  // Filtro gli slot totali con solo quelli che mi servono
  filterSlots() {
    moment.locale('it');
    const giorno = this.date.format('dddd');
    this.slots = this.allSlots.filter(slot => slot.day === giorno);
  }

  // Recupero gli slot da BE
  getSlots() {
    if (this.allSlots.length === 0) {
      this.slotService.getAllSlot(this.prestazione._id)
        .pipe(
          catchError(error => {
            console.error("Errore nel recupero degli slot", error);
            return throwError(error);
          })
        )
        .subscribe((response) => {
          this.allSlots = response.slot;
          this.filterSlots();
        });
    }

    this.filterSlots();
  }

  // Old Recupera le prenotazioni backup
  getOldPrenotazioni() {
    this.prenotazioneService.getPrenotazioni(this.date.format('YYYY-MM-DD'), this.prestazione._id)
      .pipe(
        map(responseData => this.mapPrenotazioni(responseData.prenotazioni)),
        catchError(error => {
          console.error("Errore nel recupero delle prenotazioni", error);
          return throwError(error);
        })
      )
      .subscribe();
  }


  getPrenotazioni() {
    // Se le prenotazioni sono vuote le recupero tutte
    if (this.allPrenotazioni.length < 1 && this.endCache === null) {
      this.prenotazioneService.getAllPrenotazioni(this.prestazione._id)
        .pipe(
          catchError(error => {
            console.error("Errore nel recupero delle prenotazioni", error);
            return throwError(error);
          })
        )
        .subscribe((response) => {
          // Recupero la data dalla quale sono state recuperate tutte le prenotazioni
          this.endCache = moment(response.endCache).format('YYYY-MM-DD');
          // Recupero tutte le prenotazioni
          this.allPrenotazioni = response.prenotazioni;
          this.filterPrenotazioni();
        });
    }

    this.filterPrenotazioni();
  }

  // Filtro gli slot totali con solo quelli che mi servono
  filterPrenotazioni() {
    const giorno = this.date.format('YYYY-MM-DD');
    this.events = this.allPrenotazioni.find(item => item.day == giorno)?.prenotazioni ?? [];

    if (this.events.length < 1 && moment(this.date).isBefore(this.endCache)) {
      this.getOldPrenotazioni();
    } else {
      this.mapPrenotazioni(this.events);
    }
  }

  // Mappa le prenotazioni
  mapPrenotazioni(prenotazioni: Prenotazione[]) {
    this.events = prenotazioni.map(prenotazione => {
      const data = new Date(prenotazione.inizio);
      const ore = data.getHours().toString().padStart(2, '0');
      const minuti = data.getMinutes().toString().padStart(2, '0');
      const name = prenotazione.nome + ' ' + (prenotazione.cognome ? prenotazione.cognome : '');
      const abbonamento = prenotazione.abbonamento ? 'ABB.' : '';

      return {
        prenotazione: prenotazione,
        name: name,
        abbonamento: abbonamento,
        startTime: `${ore}:${minuti}`,
        durationMinute: 20
      };
    });
  }

  mapSinglePrenotazione(prenotazione: Prenotazione) {
    const data = new Date(prenotazione.inizio);
    const ore = data.getHours().toString().padStart(2, '0');
    const minuti = data.getMinutes().toString().padStart(2, '0');
    const name = prenotazione.nome + ' ' + (prenotazione.cognome ? prenotazione.cognome : '');
    const abbonamento = prenotazione.abbonamento ? 'ABB.' : '';

    return {
      prenotazione: prenotazione,
      name: name,
      abbonamento: abbonamento,
      startTime: `${ore}:${minuti}`,
      durationMinute: 20
    };
  }

  // Trova un evento per un dato orario
  findEventByTime(startTime: string): {
    prenotazione: any,
    name: string,
    abbonamento: string,
    startTime: string,
    durationMinute: number
  } | null {
    const foundEvent = this.events.find(event => event.startTime === startTime);
    return foundEvent || null;
  }

  /*
  Eventi per azioni a FE
  */

  // Cambia la potenza
  onChangePotenza(id: string) {
    this.prestazioneService.toggleChangePotenza(id)
      .pipe(
        switchMap((response) => {
          console.log('onChangePotenza');
          this.isHighlightRed = !this.isHighlightRed;
          return of(response);
        })
      )
      .subscribe({
        error: (error) => {
          console.error("Errore nella creazione del timer della prenotazione", error);
        }
      });
  }

  // Avvia il timer per una prenotazione
  onStartTimer(id: string) {
    const timer = moment().format('YYYY-MM-DD HH:mm:ss');

    this.prenotazioneService.updateTimer(id, timer)
      .pipe(
        switchMap((response) => {
          console.log("Timer partito", response);

          // Aggiorno l'evento a cui ho fatto partire il timer
          const eventToUpdate = this.events.find(e => e.prenotazione._id === id);
          if (eventToUpdate) {
            // Aggiorni l’attributo prestazione con response.prestazione
            eventToUpdate.prenotazione = response.prenotazione;
          }

          //Aggiungo la prenotazione alla lista globale di prenotazioni
          const dayKey = moment(response.prenotazione.inizio).format('YYYY-MM-DD');
          const allPrenotazioniFind = this.allPrenotazioni.find(item => item.day === dayKey);
          if (allPrenotazioniFind) {
            const prenotazioneToUpdate = allPrenotazioniFind.prenotazioni.find((prenotazione: {
              _id: string;
            }) => prenotazione._id === id);
            if (prenotazioneToUpdate) {
              // Controllo se è stato spento o acceso il timer
              if (prenotazioneToUpdate.timer) {
                prenotazioneToUpdate.timerEnd = response.prenotazione.timerEnd;
              } else {
                prenotazioneToUpdate.timer = response.prenotazione.timer;
              }
            }
          }

          return of(response);
        })
      )
      .subscribe({
        error: (error) => {
          console.error("Errore nella creazione del timer della prenotazione", error);
        }
      });
  }

  /*
  Calcolo rendering a FE
  */

  // Calcola il tempo trascorso
  getTimePassed(timer: string, timerEnd: string | null, prenotazione: any | null): string {

    // Se il timer è stato fermato mostro il tempo trascorso
    const now = timerEnd != null ? moment(timerEnd) : moment();

    const timerDate = moment(timer);
    const duration = moment.duration(now.diff(timerDate));
    let minutes = Math.floor(duration.asMinutes());
    let seconds = Math.floor(duration.asSeconds()) % 60;

    if (this.slotDurationMinute - minutes <= 0) {
      minutes = this.slotDurationMinute;
      seconds = 0;
    }

    // Se supera il tempo medio ferma il timer
    if (prenotazione !== null && prenotazione.tempoMedio !== null && prenotazione.tempoMedio !== undefined && prenotazione.tempoMedio - minutes <= 0) {
      minutes = prenotazione.tempoMedio;
      seconds = 0;
    }

    const minutesStr = String(minutes).padStart(2, '0');
    const secondsStr = String(seconds).padStart(2, '0');

    return `${minutesStr}:${secondsStr}`;
  }

  // Imposta le variabili del calendario in base alla prestazione
  setCalendarVariablesBasedOnPerformance() {
    // Imposta la durata dello slot in base alla durata della prestazione, o usa il valore predefinito di 20 minuti se non è specificata
    this.slotDurationMinute = this.prestazione?.durata || 20;

    // Imposta il titolo della prestazione in base al nome della prestazione, o usa il valore predefinito 'Nome' se non è specificato
    this.titlePrestazione = this.prestazione?.nome || 'Nome';

    // Imposta la stanza in base alla descrizione della prestazione, o usa il valore predefinito '(descrizione)' se non è specificata
    this.stanza = this.prestazione?.descrizione || '(descrizione)';

    // Imposta il flag isRed in base al valore di changePotenza della prestazione, o usa il valore predefinito false se non è specificato
    this.isHighlightRed = this.prestazione?.changePotenza || false;
  }

  // Calcola lo stile dell'evento
  getEventClass(event: any): string {

    let classe = 'event';

    // In base al tipo di utente cambia il colore
    if (event.isUser) {
      classe += ' bg-green-500 ';
    } else {
      classe += ' bg-blue-500 ';
    }

    // Se il timer dell'evento è in esecuzione, aggiungi la classe di animazione
    if (event.timer &&
      event.timerEnd === undefined &&
      this.getTimePassed(event.timer, null, event) !== this.prestazione.durata?.toString().padStart(2, '0') + ':00' &&
      this.getTimePassed(event.timer, null, event) !== event.tempoMedio?.toString().padStart(2, '0') + ':00') {
      classe += ' animate-blink bg-yellow-400';
    }

    return classe;
  }

  // Aggiorna la posizione del time-indicator
  updateTimePosition() {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    const startMinutes = this.oraApertua * 60;
    let adjustedMinutes = totalMinutes - startMinutes;
    const intervalDuration = (this.oraChiusura - this.oraApertua) * 60;

    if (adjustedMinutes < 0) {
      adjustedMinutes = 0;
    } else if (adjustedMinutes > intervalDuration) {
      adjustedMinutes = intervalDuration;
    }

    // todo porcheria per la linea rossa (dovrebbe prendere in automatico l'altezza)
    switch (this.prestazione.descrizione) {
      case 'Lettino Alta':
        this.timePosition = 1.91837838 * adjustedMinutes;
        break;
      case 'Doccia':
      case 'Viso':
        this.timePosition = 1.944 * adjustedMinutes;
        break;
      // Sia lettini a bassa che a Led
      default:
        this.timePosition = 1.94366667 * adjustedMinutes;
        break;
    }
  }

  // Aggiorna la posizione del tempo ogni minuto
  updateTimePositionEveryMinute(): void {
    // Imposta un intervallo per chiamare la funzione updateTimePosition ogni minuto
    this.timerInterval = setInterval(() => {
      Promise.resolve().then(() => this.updateTimePosition());
    }, 60000); // 60000 millisecondi equivalgono a 1 minuto
  }

  // Funzione per il controllo se un slot è bloccato o no
  isBlocked(slot: any): boolean {
    if (!this.prestazione?.blockedSlots) {
      return false;
    }

    // Creiamo il momento corrispondente
    const date = moment(`${this.date.format('YYYY-MM-DD')} ${slot.startTime}`).locale('en');

    // Confrontiamo con gli slot bloccati della prestazione
    return this.prestazione.blockedSlots.includes(date.toString());
  }

  formatPrice(prezzo: number): string {
    return prezzo.toFixed(2);
  }

}
